import React from "react";
import {
  AddOutlined,
  UploadFileOutlined,
  MenuBookOutlined,
  FolderOpenOutlined,
  SmartToyOutlined,
  Inventory2Outlined,
  CompareArrowsOutlined,
  ArrowForward
} from "@mui/icons-material";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import "./LandingNew.scss";
import { useNavigate } from "react-router-dom";
import useProfile from "common/hooks/useProfile";

const quickActions = [
  {
    icon: <AddOutlined />,
    label: "New MOC",
    route: "/management-of-change/create"
  },
  {
    icon: <UploadFileOutlined />,
    label: "Upload Drawing",
    route: "/processing-hub"
  },
  {
    icon: <MenuBookOutlined />,
    label: "Browse Knowledge Library",
    route: "/knowledge-library"
  },
  {
    icon: <FolderOpenOutlined />,
    label: "Open Documents",
    route: "/documents"
  }
];

const processingCapabilities = [
  "Drawing Upload",
  "Document Processing",
  "Drawing Upload",
  "P&ID'S",
  "Tag Extraction",
  "Meta Data Validation"
];

const workspaceCards = [
  {
    icon: <MenuBookOutlined />,
    title: "Knowledge Library",
    description:
      "Central repository for industry standards, internal procedures, and technical documentation.",
    outcomes: [
      "Engineering Standards",
      "Internal Procedures",
      "Technical Wikis"
    ],
    cta: "Browse Library",
    route: "/knowledge-library"
  },
  {
    icon: <SmartToyOutlined />,
    title: "Knowledge Agents",
    description:
      "Domain-specific AI assistants trained on your engineering standards and project data.",
    outcomes: ["Standard Q&A", "Code Compliance", "Technical Summarization"],
    cta: "Chat with Agent",
    route: "/knowledge-agent/all-details"
  },
  {
    icon: <Inventory2Outlined />,
    title: "Packaging",
    description:
      "Consolidate engineering findings, drawings, and metadata into formal MOC and review packages.",
    outcomes: ["Document Bundling", "Package Validation", "Export to PDF/Word"],
    cta: "Chat with Agent",
    route: "/packaging-workspace"
  },
  {
    icon: <CompareArrowsOutlined />,
    title: "Management of Change",
    description:
      "AI-assisted workflow for technical and organizational change management and risk assessment.",
    outcomes: ["Change Initiation", "Impact Assessment"],
    cta: "Launch MOC",
    route: "/management-of-change"
  },
  {
    icon: <CompareArrowsOutlined />,
    title: "Ontology",
    description: "AI-assisted ontology management for CQ creation.",
    outcomes: ["Ontology Creation", "Ontology Management"],
    cta: "Create Ontology",
    route: "/create-ontology"
  }
];

const LandingNew: React.FC = () => {
  const navigate = useNavigate();
  const { givenName, displayName } = useProfile();
  const userName = givenName || displayName;
  return (
    <>
      {/* <HeaderNew showSearch={false} /> */}
      <div className="landing-new">
        {/* Hero Banner */}
        <section className="hero-banner">
          <div className="hero-overlay" />
          <div className="hero-content">
            <span className="hero-label">
              Welcome back{userName ? `, ${userName}` : ""}
            </span>
            <h1 className="hero-title">
              Unified workspace for
              <br />
              engineering intelligence
            </h1>
            <p className="hero-subtitle">
              Access engineering workflows, technical knowledge, documents,
              AI-assisted analysis, and Management of Change from a single
              platform.
            </p>
            <div className="hero-actions">
              <button
                className="btn-primary"
                onClick={() => navigate("/knowledge-library")}
              >
                Explore Library
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate("/ai-search-hub")}
              >
                Search Engineering Knowledge
              </button>
            </div>
          </div>
          {/* <div className="hero-tag">
            <span>Gas Compression</span>
            <span>System</span>
          </div> */}
        </section>

        {/* Quick Actions */}
        <section className="quick-actions">
          <span className="quick-actions__label">QUICK ACTION:</span>
          <div className="quick-actions__list">
            {quickActions.map((action) => (
              <button
                className="quick-action"
                key={action.label}
                onClick={() => navigate(action.route)}
              >
                <span className="quick-action__icon">{action.icon}</span>
                <span className="quick-action__text">{action.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Processing Hub */}
        <section className="processing-hub-card">
          <div className="processing-hub-card__top">
            <div className="processing-hub-card__icon">
              <BackupOutlinedIcon />
            </div>
          </div>
          <h2 className="processing-hub-card__title">Processing Hub</h2>
          <p className="processing-hub-card__description">
            Centralized intake for engineering documents, Upload drawings,
            P&amp;IDs, specifications, and datasheets for automated AI
            Processing and preparation.
          </p>
          <span className="processing-hub-card__section-label">
            CAPABILITIES
          </span>
          <div className="processing-hub-card__pills">
            {processingCapabilities.map((cap, index) => (
              <span
                className="processing-hub-card__pill"
                key={`${cap}-${index}`}
              >
                {cap}
              </span>
            ))}
          </div>
          <button
            className="processing-hub-card__cta"
            onClick={() => navigate("/processing-hub")}
          >
            <span>Open Processing Hub</span>
            <ArrowForward fontSize="small" />
          </button>
        </section>

        {/* Workspace Cards */}
        <section className="workspace-cards">
          {workspaceCards.map((card) => (
            <div className="workspace-card" key={card.title}>
              <div className="workspace-card__icon">{card.icon}</div>
              <h3 className="workspace-card__title">{card.title}</h3>
              <p className="workspace-card__description">{card.description}</p>
              <span className="workspace-card__section-label">OUTCOMES</span>
              <ul className="workspace-card__outcomes">
                {card.outcomes.map((outcome) => (
                  <li className="workspace-card__outcome" key={outcome}>
                    {outcome}
                  </li>
                ))}
              </ul>
              <button
                className="workspace-card__cta"
                onClick={() => navigate(card.route)}
              >
                <span>{card.cta}</span>
                <ArrowForward fontSize="small" />
              </button>
            </div>
          ))}
        </section>
      </div>
    </>
  );
};

export default LandingNew;
